import React, { useState, useEffect, useRef } from 'react';
import { Steps, Button, Space, Card } from 'antd';
import { LeftOutlined, RightOutlined, CheckOutlined } from '@ant-design/icons';

const { Step } = Steps;

/**
 * Componente Wizard reutilizável para fluxos multi-etapas
 * 
 * @param {Array} steps - Array de objetos com: { title, description, content: ReactNode }
 * @param {Function} onFinish - Callback chamado ao concluir todas as etapas
 * @param {Object} initialData - Dados iniciais do formulário
 */
const SiteWizard = ({ steps, onFinish, onCancel, initialData = {} }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(initialData);
    const wizardTopRef = useRef(null);

    // Scroll para o topo quando mudar de etapa
    useEffect(() => {
        if (wizardTopRef.current) {
            wizardTopRef.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
            });
        }
    }, [currentStep]);

    const handleNext = (stepData) => {
        // Mesclar dados da etapa atual
        setFormData(prev => ({ ...prev, ...stepData }));
        
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleFinish = async (finalStepData) => {
        const completeData = { ...formData, ...finalStepData };
        await onFinish(completeData);
    };

    const handleStepClick = (step) => {
        // Permitir navegar para etapas anteriores
        if (step < currentStep) {
            setCurrentStep(step);
        }
    };

    return (
        <div ref={wizardTopRef} style={{ width: '100%' }}>
            {/* Steps Header */}
            <Card style={{ marginBottom: '24px' }}>
                <Steps 
                    current={currentStep}
                    onChange={handleStepClick}
                    style={{ marginBottom: 0 }}
                >
                    {steps.map((step, index) => (
                        <Step 
                            key={index}
                            title={step.title}
                            description={step.description}
                            style={{ cursor: index < currentStep ? 'pointer' : 'default' }}
                        />
                    ))}
                </Steps>
            </Card>

            {/* Step Content */}
            <div style={{ minHeight: '400px', marginBottom: '24px' }}>
                {React.cloneElement(steps[currentStep].content, {
                    onNext: handleNext,
                    onBack: handleBack,
                    onFinish: handleFinish,
                    formData: formData,
                    isFirstStep: currentStep === 0,
                    isLastStep: currentStep === steps.length - 1,
                })}
            </div>

            {/* Navigation Footer */}
            <Card>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button 
                        onClick={onCancel}
                        disabled={false}
                    >
                        Cancelar
                    </Button>
                    
                    <Space>
                        {currentStep > 0 && (
                            <Button 
                                icon={<LeftOutlined />}
                                onClick={handleBack}
                            >
                                Voltar
                            </Button>
                        )}
                    </Space>
                </Space>
            </Card>
        </div>
    );
};

export default SiteWizard;
